import React from 'react'
import { observer } from 'mobx-react'
import {
  SingleItemSelector,
  SelectItem,
} from 'admin/staffing/components/WorkspacePostingDialog/SingleItemSelector'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  Form,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import Skill from 'iam/models/Skill'
import { pluralize } from 'utils/pluralization-util'
import { WorkspaceInvitationViewModel } from './WorkspaceInvitationViewModel'

interface WorkspaceInvitationDialogProps {
  vm: WorkspaceInvitationViewModel
}

export const WorkspaceInvitationDialog = observer(
  ({ vm }: WorkspaceInvitationDialogProps) => {
    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>Invite to workspace</ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              Fill in the emails of the users you'd like to invite, and complete
              the required information.
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="workspaceInvitation" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer
                  label="Email address"
                  error={vm.validation.getError('emailAddressTextArea')}
                >
                  <TextAreaContainer
                    model={vm}
                    field="emailAddressTextArea"
                    rows={3}
                    placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
                    help="Enter one or more email addresses, separated by commas."
                    data-test="workspace-invitation-email-textarea"
                  />
                </FormGroupContainer>
              </FormRow>

              <FormRow>
                <FormGroupContainer
                  label="Workspace"
                  error={vm.validation.getError('workspace')}
                >
                  <SingleItemSelector
                    model={vm.workspace}
                    itemOptions={vm.workspaces}
                    itemPrefix={(item) => (
                      <Avatar
                        subject={{
                          avatar: item?.branding.logos.icon,
                        }}
                        size={24}
                      />
                    )}
                    onChange={(value) =>
                      vm.setWorkspace(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select workspace"
                    disabled={vm.submit.pending}
                    dataTest="workspace-invitation-workspace-selector"
                  />
                </FormGroupContainer>
              </FormRow>

              {vm.workspace && (
                <>
                  <FormRow>
                    <FormGroupContainer
                      label="Role"
                      error={vm.validation.getError('workspaceRole')}
                    >
                      <SingleItemSelector
                        model={vm.workspaceRole}
                        itemOptions={vm.workspaceRoles}
                        onChange={(value) =>
                          vm.setWorkspaceRole(
                            value === undefined ? null : value,
                          )
                        }
                        itemToString={(item) => (item ? item.name : '')}
                        placeholder="Select workspace role"
                        disabled={vm.submit.pending}
                        dataTest="workspace-invitation-role-selector"
                      />
                    </FormGroupContainer>
                  </FormRow>

                  <FormRow>
                    <FormGroupContainer label="Skills" sublabel="(optional)">
                      <SkillsSelector
                        vm={vm}
                        disabled={vm.submit.pending}
                        dataTest="workspace-invitation-skills-selector"
                      />
                    </FormGroupContainer>
                  </FormRow>

                  <FormRow>
                    <FormGroupContainer label="Team" sublabel="(optional)">
                      <SingleItemSelector
                        model={vm.team}
                        itemOptions={vm.teams}
                        onChange={(value) =>
                          vm.setTeam(value === undefined ? null : value)
                        }
                        itemToString={(item) => (item ? item.name : '')}
                        placeholder="Select team"
                        disabled={vm.submit.pending}
                        dataTest="workspace-invitation-team-selector"
                      />
                    </FormGroupContainer>
                  </FormRow>

                  {vm.team && (
                    <FormRow>
                      <FormGroupContainer
                        label="Team role"
                        error={vm.validation.getError('teamRole')}
                      >
                        <SingleItemSelector
                          model={vm.teamRole}
                          itemOptions={vm.teamRoles}
                          onChange={(value) =>
                            vm.setTeamRole(value === undefined ? null : value)
                          }
                          itemToString={(item) => (item ? item.name : '')}
                          placeholder="Select team role"
                          disabled={vm.submit.pending}
                          dataTest="workspace-invitation-team-role-selector"
                        />
                      </FormGroupContainer>
                    </FormRow>
                  )}
                </>
              )}

              <FormRow>
                <FormGroupContainer label="Portal">
                  <SingleItemSelector
                    model={vm.portal}
                    itemOptions={vm.portals}
                    onChange={(value) =>
                      value ? vm.setPortal(value) : undefined
                    }
                    itemToString={(item) => item}
                    placeholder="Select portal to link to"
                    disabled={vm.submit.pending}
                    dataTest="workspace-invitation-portal-selector"
                  />
                </FormGroupContainer>
              </FormRow>
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="workspaceInvitation"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="workspace-invitation-submit"
            >
              {`Send ${pluralize('invite', vm.selectedUsers.size)}`}
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)

interface SkillsSelectorProps {
  vm: WorkspaceInvitationViewModel
  disabled?: boolean
  dataTest?: string
}

const SkillsSelector = observer(function ({
  vm,
  disabled,
  dataTest,
}: SkillsSelectorProps) {
  const itemToString = (item: Skill | undefined) => item?.name || ''

  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableSkills, (item, text) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.availableSkills],
  )

  const change = React.useCallback(
    (skills: Skill[]) => (skills ? vm.setSkills(skills) : undefined),
    [vm],
  )

  const itemProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...itemProps}
      itemToString={itemToString}
      value={vm.skills}
      onChange={change}
      disabled={disabled}
      multi={true}
      persist
      placeholder="Select skills"
      data-test={dataTest}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem key={item.id} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
})
